import useScrollReveal from '../useScrollReveal';
import { sectionStyles, isCardTransparent, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function ColumnsSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(null, s.dark);
    const cols = config.columns || 3;
    const gridCls = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3';

    if (items.length === 0) return null;

    const variant = config.variant || 'simple';
    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    /* ── cards variant ── */
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i} className={`rounded-2xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms', backgroundColor: tp ? 'rgba(255,255,255,0.06)' : '#ffffff' }}>
                                {item.icon && (
                                    <span className="text-3xl w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: hexToRgba(s.accent, 0.12) }}>
                                        {item.icon}
                                    </span>
                                )}
                                {item.title && <h3 className="font-bold text-lg" style={{ color: s.text }}>{item.title}</h3>}
                                {item.text && <p className="text-sm mt-2 leading-relaxed" style={{ color: s.textSub }}>{item.text}</p>}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── asymmetric (zigzag) variant ── */
    if (variant === 'asymmetric') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-8">
                        {items.map((item, i) => (
                            <div key={i} className={`flex items-start gap-4 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''} transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                                {item.icon && (
                                    <span className="text-2xl shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                        {item.icon}
                                    </span>
                                )}
                                <div className={i % 2 === 1 ? 'md:text-right' : ''}>
                                    {item.title && <h3 className="font-bold text-base" style={{ color: s.text }}>{item.title}</h3>}
                                    {item.text && <p className="text-sm mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.text}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── simple (default) variant ── */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${gridCls} gap-8`}>
                    {items.map((item, i) => (
                        <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                            <div className="flex items-start gap-3">
                                {item.icon && (
                                    <span className="text-2xl shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.07) }}>
                                        {item.icon}
                                    </span>
                                )}
                                <div>
                                    {item.title && <h3 className="font-bold text-base" style={{ color: s.text }}>{item.title}</h3>}
                                    {item.text && <p className="text-sm mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.text}</p>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
